// The replay bot: an outlined, see-through player figure running the map's record (a GhostPath),
// with the record holder's name over its head like a player name in CS:S. It loops: runs the
// record, waits a moment at the end, and starts again. Not solid, and it fades out when the camera
// is inside or right next to it.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { GHOST_STRIDE, type GhostPath } from '../online/ghost';
import { makeSourceSpace, sourceToThree } from './coords';

const TICK_RATE = 100;
/** Ticks the bot waits at the finish before running again. */
const HOLD_TICKS = 200;
const COLOR = 0x7fd3ff;
/** Standing and crouched heights (the player's hull). */
const STAND = 72;
const DUCK = 54;
/** Camera closer than this (units) hides the bot; it's fully shown from FADE_NEAR + FADE_RANGE. */
const FADE_NEAR = 40;
const FADE_RANGE = 80;
/** The name tag shows only up close (about 30 steps), fading out over the last TAG_FADE units. */
const TAG_FAR = 1000;
const TAG_FADE = 200;

/** A blocky player figure in Source units: feet at the origin, facing +X, 72 tall. */
function figureGeometry(): THREE.BufferGeometry {
  const box = (sx: number, sy: number, sz: number, x: number, y: number, z: number) =>
    new THREE.BoxGeometry(sx, sy, sz).translate(x, y, z).toNonIndexed();
  const parts = [
    box(7, 7, 34, 0, 5, 17), // legs
    box(7, 7, 34, 0, -5, 17),
    box(10, 22, 24, 0, 0, 46), // body
    box(6, 6, 24, 1, 14, 45), // arms
    box(6, 6, 24, 1, -14, 45),
    new THREE.IcosahedronGeometry(7, 0).translate(0, 0, 65), // head
  ];
  const g = mergeGeometries(parts)!;
  for (const p of parts) p.dispose();
  return g;
}

/** The name tag: name on top, the record time under it (CS:S name colours: light text, dark edge). */
function nameTexture(name: string, time: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const g = c.getContext('2d')!;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  const line = (text: string, y: number, size: number, color: string) => {
    g.font = `bold ${size}px Tahoma, Verdana, sans-serif`;
    g.lineWidth = 7;
    g.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    g.strokeText(text, 256, y, 500);
    g.fillStyle = color;
    g.fillText(text, 256, y, 500);
  };
  line(name, 44, 46, '#cfe9ff');
  line(time, 98, 34, '#ffd27a');
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

export class ReplayBot {
  /** Source-space container (children use Source units and axes). */
  private readonly space = new THREE.Group();
  private readonly figure = new THREE.Group();
  private readonly fill: THREE.MeshBasicMaterial;
  private readonly lines: THREE.LineBasicMaterial;
  private readonly tag: THREE.Sprite;
  private readonly tagMat: THREE.SpriteMaterial;
  private path: GhostPath | null = null;
  /** Playback position in ticks. */
  private t = 0;
  private readonly tmp = new THREE.Vector3();
  /** Shown at all (Settings). */
  enabled = true;
  /** Hidden while its new name tag compiles (GameRenderer.prewarm). */
  waiting = false;

  constructor(scene: THREE.Scene) {
    makeSourceSpace(this.space);
    const geo = figureGeometry();
    this.fill = new THREE.MeshBasicMaterial({
      color: COLOR,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
    });
    this.lines = new THREE.LineBasicMaterial({ color: COLOR, transparent: true, opacity: 0.95 });
    const body = new THREE.Mesh(geo, this.fill);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 25), this.lines);
    body.renderOrder = edges.renderOrder = 2;
    this.figure.add(body, edges);
    this.tagMat = new THREE.SpriteMaterial({ transparent: true, depthWrite: false, sizeAttenuation: false });
    this.tag = new THREE.Sprite(this.tagMat);
    // Fixed size on screen, like CS:S names.
    this.tag.scale.set(0.3, 0.075, 1);
    this.tag.renderOrder = 3;
    this.space.add(this.figure, this.tag);
    this.space.visible = false;
    scene.add(this.space);
  }

  /** Run `path` (from its start), labelled with the record holder's name and time. */
  set(path: GhostPath, name: string, time: string) {
    this.path = path;
    this.t = 0;
    this.tagMat.map?.dispose();
    this.tagMat.map = nameTexture(name, time);
    this.tagMat.needsUpdate = true;
  }

  clear() {
    this.path = null;
    this.space.visible = false;
  }

  /** Not shown this frame (spectating the same run). */
  hide() {
    this.space.visible = false;
  }

  /** Advance by `dt` seconds and place the bot; `camera` is the camera position (three.js units). */
  update(dt: number, camera: THREE.Vector3) {
    const p = this.path;
    if (!p || !this.enabled || this.waiting) {
      this.space.visible = false;
      return;
    }
    this.t += dt * TICK_RATE;
    const last = p.ticks - 1;
    if (this.t > last + HOLD_TICKS) this.t = 0;
    const at = Math.min(this.t, last);
    const i = Math.floor(at);
    const f = at - i;
    const a = i * GHOST_STRIDE;
    const b = Math.min(i + 1, last) * GHOST_STRIDE;
    const d = p.data;
    const x = d[a] + (d[b] - d[a]) * f;
    const y = d[a + 1] + (d[b + 1] - d[a + 1]) * f;
    const z = d[a + 2] + (d[b + 2] - d[a + 2]) * f;
    // Shortest way round between the two yaws.
    let dy = d[b + 3] - d[a + 3];
    dy -= Math.round(dy / (2 * Math.PI)) * 2 * Math.PI;
    const yaw = d[a + 3] + dy * f;
    const height = d[a + 4] ? DUCK : STAND;

    this.figure.position.set(x, y, z);
    this.figure.rotation.set(0, 0, yaw);
    this.figure.scale.set(1, 1, height / STAND);
    this.tag.position.set(x, y, z + height + 14);

    // Fade out when the camera is in or right next to it.
    const dist = sourceToThree(this.tmp, x, y, z + height / 2).distanceTo(camera);
    const k = Math.max(0, Math.min(1, (dist - FADE_NEAR) / FADE_RANGE));
    this.space.visible = k > 0;
    this.fill.opacity = 0.16 * k;
    this.lines.opacity = 0.95 * k;
    const tag = k * Math.max(0, Math.min(1, (TAG_FAR - dist) / TAG_FADE));
    this.tag.visible = tag > 0;
    this.tagMat.opacity = tag;
  }
}
